import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { kebabCase } from '@makemydeal/dr-common-utils';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { FLEX_BUY_TERM_LIST } from '@makemydeal/dr-dash-store/constants';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GENERIC_LAST_FIELD_CHANGED_ERROR, TERM } from '../../constants';
import { parseSelectedTermMonths } from '../../terms/common/utils';
import { flexBuyPercentOptions, flexBuyTermOptions } from '../constants';
import { getCurrentMonth } from '../utils';
import { FINANCE, FlexBuyAvailableMonths } from '@makemydeal/dr-platform-types';
import type { DealTabs } from '@makemydeal/dr-shared-types';

export const useFlexBuyTermField = ({ tab }: { tab?: DealTabs }) => {
    const dispatch = useDispatch();
    const failed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.SELECTED_TERM));
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const termId = useSelector((state) => offerReduxSelectors.getTermIdByOfferType(state, offerType));
    const parsedTerm = parseSelectedTermMonths(termId);
    const currentFlexBuyPayment = useSelector(offerReduxSelectors.getCurrentFlexBuyPayments);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const offer = useSelector(offerReduxSelectors.getOffer);
    const downPayment = useSelector(offerReduxSelectors.getDownPayment);

    const isFlexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const useNewTermIdStandard = useSelector(featureToggleSelectors.useNewTermIdStandard);

    const currentFlexBuyPaymentGridTerms = useSelector(offerReduxSelectors.getFlexBuyPaymentGridTerms);
    const currentFlexBuyPaymentGridPercents = useSelector(offerReduxSelectors.getFlexBuyPaymentGridPercents);
    const currentFlexBuyPaymentGridCashDowns = useSelector(offerReduxSelectors.getFlexBuyPaymentGridCashDowns);

    const currentMonths = getCurrentMonth({ parseSelectedTermId: parsedTerm });

    const initialPaymentDiscount = Number(
        currentFlexBuyPayment?.initialPaymentDiscount ?? flexBuyPercentOptions[FlexBuyAvailableMonths.MONTH_66][0].value
    );

    const [termNumber, setTermNumber] = useState(currentMonths);

    const failedMessage = failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined;

    const updateSelectedTerm = (
        termId: string,
        { requestPayment, meta }: { requestPayment?: boolean; meta?: Record<string, unknown> }
    ) => {
        dispatch(
            offerReduxActionCreators.selectedTerm(termId, {
                suppressAnalytics: true,
                payment: requestPayment,
                meta
            })
        );
    };

    useEffect(() => {
        if (currentMonths && termNumber !== currentMonths) setTermNumber(currentMonths);
    }, [currentMonths, termNumber]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(tab, kebabCase(`${TERM} (Monthly)`), failed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, tab]);

    const updateFlexBuyPayment = (value: number) => {
        dispatch(
            offerReduxActionCreators.updateFlexBuyPaymentPercent(value, {
                requestPayment: false
            })
        );
    };

    const handleChangeTerm: InterstateOnChangeCallback<SelectInputEventValue> = (e) => {
        const newTermValue = e.target.value;

        if (!newTermValue) return;
        const parsedNewTermValue = Number(newTermValue);

        if (parsedNewTermValue === termNumber || isNaN(parsedNewTermValue)) {
            return;
        }

        const flexBuyPercentageFor75Months = Number(flexBuyPercentOptions[FlexBuyAvailableMonths.MONTH_75][0].value);
        const isEligibleFor75MonthDiscount =
            parsedNewTermValue === FlexBuyAvailableMonths.MONTH_75 && initialPaymentDiscount !== flexBuyPercentageFor75Months;

        setTermNumber(parsedNewTermValue);
        const basicFinanceTerm = `finance_term_${parsedNewTermValue}`;

        const newTermIdStandard = useNewTermIdStandard
            ? offerReduxSelectors.getAutoTermId(offer, parsedNewTermValue, {
                  offerType: FINANCE,
                  extension: {
                      DOWNPAYMENT: Number(downPayment),
                      FORDFLEXBUY: initialPaymentDiscount,
                      TERM: parsedNewTermValue
                  }
              })
            : null;

        const termIdToUpdate = newTermIdStandard || basicFinanceTerm;

        if (isEligibleFor75MonthDiscount) {
            updateFlexBuyPayment(flexBuyPercentageFor75Months);
        }

        const meta = isFlexBuyPaymentGridEnabled
            ? {
                  paymentRequestOverride: {
                      [FINANCE]: {
                          flexBuyDownPayments: currentFlexBuyPaymentGridCashDowns,
                          flexBuyTerms: currentFlexBuyPaymentGridTerms,
                          flexBuyPercents: currentFlexBuyPaymentGridPercents,
                          termList: FLEX_BUY_TERM_LIST
                      }
                  }
              }
            : undefined;

        updateSelectedTerm(termIdToUpdate, {
            meta
        });
    };

    return {
        label: `${TERM} (Monthly)`,
        selectProps: {
            name: kebabCase(`${TERM} (Monthly)`),
            placeholder: 'Select Term',
            onChange: handleChangeTerm,
            value: termNumber?.toString(),
            options: flexBuyTermOptions,
            errorMessage: failedMessage,
            hasError: !!failedMessage,
            displayDeselectOption: false,
            'data-testid': kebabCase(`${TERM} (Monthly)`),
            size: 'sm' as const,
            readOnly: isFlexBuyPaymentGridEnabled
        }
    };
};
